// src/pages/RegisterPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/register.css';
import logoImage from '../assets/logo.png';

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/welcome-page';
  
  // Utiliser le contexte d'authentification
  const { 
    register: authRegister, 
    login: authLogin, 
    loginPhone: authLoginPhone, 
    verifyOTP: authVerifyOTP, 
    isAuthenticated, 
    user,
    verifyPhoneNumber,
    verifyEmailAddress 
  } = useAuth();

  // États pour le mode (inscription ou connexion)
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [loginMethod, setLoginMethod] = useState('phone'); // 'email' ou 'phone'
  
  // États pour l'inscription
  const [registerData, setRegisterData] = useState({
    prenom: '',
	nom: '',
    telephone: '',
    email: '',
    acceptTerms: false,
    acceptNewsletter: false
  });

  // États pour la vérification post-inscription
  const [showVerificationStep, setShowVerificationStep] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState(''); // 'phone' ou 'email'
  const [verificationData, setVerificationData] = useState({
    value: '', // numéro ou email à vérifier
    editable: true,
    originalValue: '' // valeur originale pour comparaison
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationCodeSent, setVerificationCodeSent] = useState(false);

  // États pour la connexion
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPhone, setLoginPhone] = useState('');
  const [phoneReadOnly, setPhoneReadOnly] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpPhone, setOtpPhone] = useState('');
  
  // État pour savoir si le téléphone est verrouillé à cause d'une détection existante
  const [isPhoneLocked, setIsPhoneLocked] = useState(false);

  // États généraux
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // État pour la détection d'opérateur téléphone
  // null = pas encore saisi, 'valid' = Orange/MTN, 'invalid' = autre opérateur
  const [phoneOperator, setPhoneOperator] = useState(null);

  // Timer pour le code OTP
  const [otpTimer, setOtpTimer] = useState(0);

  // Effet pour le timer OTP
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Vérifier si l'utilisateur est déjà connecté
  useEffect(() => {
    if (isAuthenticated && user && !showVerificationStep) {
      // Rediriger si déjà connecté et pas en étape de vérification
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, user, navigate, from, showVerificationStep]);

  // Détection de l'opérateur téléphonique à partir du préfixe
  // Préfixes valides Orange/MTN au Cameroun : 64x, 65x, 67x, 68x, 69x
  const detectPhoneOperator = (phone) => {
    if (!phone || phone.length < 2) return null;
    const prefix = phone.substring(0, 2);
    const validPrefixes = ['64', '65', '67', '68', '69'];
    if (validPrefixes.includes(prefix)) return 'valid';
    if (phone.startsWith('6') && phone.length >= 2) return 'invalid';
    return null;
  };

  // Validation des champs
  const validateRegisterForm = () => {
    const errors = {};
    
    if (!registerData.prenom.trim()) {
	    errors.prenom = 'Le prénom est obligatoire';
	  }

    if (!registerData.telephone.trim()) {
      errors.telephone = 'Le numéro de téléphone est obligatoire';
    } else if (!/^6\d{8}$/.test(registerData.telephone)) {
      errors.telephone = 'Le numéro doit commencer par 6 et contenir 9 chiffres';
    } else if (!/^(64|65|67|68|69)\d{7}$/.test(registerData.telephone)) {
      errors.telephone = 'Ce numéro n\'est pas compatible. Utilisez un numéro Orange ou MTN.';
    }
    
    // Email optionnel - validation seulement si rempli
    if (registerData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerData.email)) {
      errors.email = 'Format d\'email invalide';
    }
    
    if (!registerData.acceptTerms) {
      errors.acceptTerms = 'Vous devez accepter les conditions d\'utilisation';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateLoginForm = () => {
    if (loginMethod === 'email') {
      if (!loginEmail.trim()) {
        setFieldErrors({ email: 'L\'email est obligatoire' });
        return false;
      }
      
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginEmail)) {
        setFieldErrors({ email: 'Format d\'email invalide' });
        return false;
      }
    } else {
      if (!loginPhone.trim()) {
        setFieldErrors({ phone: 'Le numéro est obligatoire' });
        return false;
      }
      
      if (!/^6\d{8}$/.test(loginPhone)) {
        setFieldErrors({ phone: 'Le numéro doit commencer par 6 et contenir 9 chiffres' });
        return false;
      }
    }
    
    setFieldErrors({});
    return true;
  };

  // Gestion de l'inscription
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!validateRegisterForm()) {
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    setFieldErrors({});

    try {
      const result = await authRegister({
        nom: registerData.nom.trim() || 'Client',  // NOUVEAU : valeur par défaut "Client"
        prenom: registerData.prenom,
        telephone: registerData.telephone,
        email: registerData.email.trim() || null,
        acceptTerms: registerData.acceptTerms,
        acceptNewsletter: registerData.acceptNewsletter
      });

      console.log('Résultat inscription:', result);

      if (result.success) {
        // Inscription réussie - passer à l'étape de vérification
        setSuccess('Inscription réussie !');
        
        // Déterminer quelle méthode de vérification utiliser
        if (registerData.telephone) {
          setVerificationMethod('phone');
          setVerificationData({
            value: registerData.telephone,
            editable: true,
            originalValue: registerData.telephone
          });
        } else if (registerData.email.trim()) {
          setVerificationMethod('email');
          setVerificationData({
            value: registerData.email.trim(),
            editable: true,
            originalValue: registerData.email.trim()
          });
        }
        
        // Afficher l'étape de vérification
        setShowVerificationStep(true);
        setError('');
        
      } else if (result.shouldLogin && result.telephone) {
        // Cas : numéro déjà utilisé → bascule vers connexion SMS
        console.log('Bascule activée : numéro existant détecté, passage en mode connexion SMS');
        setIsLoginMode(true);
        setLoginMethod('phone');
        setLoginPhone(result.telephone);
        setPhoneReadOnly(true);
        setIsPhoneLocked(true);
        setSuccess('Ce numéro est déjà enregistré. Connectez-vous pour continuer.');
        setError('');
        setFieldErrors({});
      } else {
        // Autres erreurs
        setError(result.message || 'Erreur lors de l\'inscription');
      }
    } catch (error) {
      console.log('Erreur inscription:', error);
      const errorMsg = error.message || 'Erreur lors de l\'inscription';

      if (errorMsg.includes('email') && errorMsg.includes('utilis')) {
        setFieldErrors({ email: errorMsg });
      } else if (errorMsg.includes('téléphone') || errorMsg.includes('telephone') || errorMsg.includes('utilis')) {
        console.log('📞 Bascule vers connexion SMS activée (catch)');
        setIsLoginMode(true);
        setLoginMethod('phone');
        setLoginPhone(registerData.telephone);
        setPhoneReadOnly(true);
        setSuccess('Ce numéro est déjà enregistré. Connectez-vous pour continuer.');
        setError('');
        setFieldErrors({});
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Envoyer le code de vérification
const handleSendVerificationCode = async () => {
    console.log('Tentative d\'envoi du code pour:', verificationData.value);
    console.log('Type de vérification:', verificationMethod);
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
        let result;
        
        if (verificationMethod === 'phone') {
            // Valider le format du téléphone
            if (!/^6\d{8}$/.test(verificationData.value)) {
                setError('Le numéro doit commencer par 6 et contenir 9 chiffres');
                setLoading(false);
                return;
            }
            
            console.log('Appel API verifyPhoneNumber...');
            result = await verifyPhoneNumber(verificationData.value);
            console.log('Résultat API:', result);
            
            if (result.success) {
                setVerificationCodeSent(true);
                setOtpTimer(180); // 3 minutes pour SMS
                setSuccess('Code de vérification envoyé par SMS !');
                setVerificationData(prev => ({ ...prev, editable: false }));
            }
        } else {
            // Valider le format de l'email
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(verificationData.value)) {
                setError('Format d\'email invalide');
                setLoading(false);
                return;
            }
            
            console.log('Appel API verifyEmailAddress...');
            result = await verifyEmailAddress(verificationData.value);
            console.log('Résultat API:', result);
            
            if (result.success) {
                setVerificationCodeSent(true);
                setOtpTimer(600); // 10 minutes pour email
                setSuccess('Code de vérification envoyé par email !');
                setVerificationData(prev => ({ ...prev, editable: false }));
            }
        }
        
        if (!result.success) {
            setError(result.message || 'Erreur lors de l\'envoi du code');
        }
    } catch (error) {
        console.error('Erreur complète:', error);
        setError(error.message || 'Erreur lors de l\'envoi du code');
    } finally {
        setLoading(false);
    }
};

  // Vérifier le code
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!verificationCode.trim()) {
      setError('Le code est obligatoire');
      return;
    }
    
    const expectedLength = verificationMethod === 'phone' ? 4 : 6;
    if (verificationCode.length !== expectedLength || !/^\d+$/.test(verificationCode)) {
      setError(`Le code doit contenir ${expectedLength} chiffres`);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const verifyData = {
        code: verificationCode,
        type: 'verification' // Pour différencier de la connexion OTP
      };
      
      if (verificationMethod === 'phone') {
        verifyData.telephone = verificationData.value;
      } else {
        verifyData.email = verificationData.value;
      }
      
      const result = await authVerifyOTP(verifyData);
      
      if (result.success) {
        setSuccess(verificationMethod === 'phone' ? 
          'Numéro de téléphone vérifié avec succès !' : 
          'Adresse email vérifiée avec succès !');
        
        // Rediriger après 2 secondes
        setTimeout(() => {
          navigate(from, { replace: true });
        }, 2000);
      } else {
        setError(result.message || 'Code incorrect');
      }
    } catch (error) {
      setError(error.message || 'Erreur lors de la vérification');
    } finally {
      setLoading(false);
    }
  };

  // Ignorer la vérification
  const handleSkipVerification = () => {
    setSuccess('Vous pourrez vérifier votre compte plus tard depuis votre profil.');
    
    // Afficher un avertissement
    setTimeout(() => {
      setError('⚠️ Attention : Vous devrez vérifier votre numéro ou email avant de passer commande.');
    }, 500);
    
    // Rediriger après 3 secondes
    setTimeout(() => {
      navigate(from, { replace: true });
    }, 3000);
  };

  // Modifier le numéro/email avant envoi
  const handleModifyVerificationData = () => {
    setVerificationData(prev => ({ ...prev, editable: true }));
    setVerificationCodeSent(false);
    setVerificationCode('');
    setOtpTimer(0);
    setError('');
    setSuccess('');
  };

  // Gestion de la connexion - Étape 1: Demande OTP
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateLoginForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      let result;
      
      if (loginMethod === 'email') {
        result = await authLogin(loginEmail);
        
        if (result.success) {
          setShowOtpInput(true);
          setOtpEmail(loginEmail);
          setOtpTimer(600); // 10 minutes
          setSuccess('Code de connexion envoyé ! Vérifiez votre email.');
          setFieldErrors({});
        }
      } else {
        result = await authLoginPhone(loginPhone);
        
        if (result.success) {
          setShowOtpInput(true);
          setOtpPhone(loginPhone);
          setOtpTimer(180); // 3 minutes pour SMS
          setSuccess('Code de connexion envoyé par SMS !');
          setFieldErrors({});
        }
      }
      
      if (!result.success) {
        if (result.message.includes('Aucun compte')) {
          setFieldErrors({ [loginMethod === 'email' ? 'email' : 'phone']: 'Aucun compte associé' });
        } else if (result.message.includes('Trop de tentatives')) {
          setError('Trop de tentatives. Veuillez réessayer plus tard.');
        } else {
          setError(result.message || 'Erreur lors de la connexion');
        }
      }
    } catch (error) {
      setError(error.message || 'Erreur lors de la connexion');
    } finally {
      setLoading(false);
    }
  };

  // Gestion de la connexion - Étape 2: Vérification OTP
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    
    if (!otpCode.trim()) {
      setFieldErrors({ otp: 'Le code est obligatoire' });
      return;
    }
    
    const expectedLength = loginMethod === 'email' ? 6 : 4;
    if (otpCode.length !== expectedLength || !/^\d+$/.test(otpCode)) {
      setFieldErrors({ otp: `Le code doit contenir ${expectedLength} chiffres` });
      return;
    }
    
    setLoading(true);
    setError('');
    setFieldErrors({});
    
    try {
      const verifyData = {
        code: otpCode
      };
      
      if (loginMethod === 'email') {
        verifyData.email = otpEmail;
      } else {
        verifyData.telephone = otpPhone;
      }
      
      const result = await authVerifyOTP(verifyData);
      
      if (result.success) {
        setSuccess('Connexion réussie !');
        
        // Rediriger après 1 seconde
        setTimeout(() => {
          navigate(from, { replace: true });
        }, 1000);
      } else {
        if (result.message.includes('incorrect')) {
          setFieldErrors({ otp: 'Code incorrect' });
        } else if (result.message.includes('expiré')) {
          setFieldErrors({ otp: 'Code expiré. Demandez un nouveau code.' });
          setOtpTimer(0);
        } else {
          setError(result.message || 'Erreur lors de la vérification');
        }
      }
    } catch (error) {
		console.error("Erreur OTP:", error);
		
      if (error.message.includes('incorrect')) {
        setFieldErrors({ otp: 'Code incorrect' });
      } else if (error.message.includes('expiré')) {
        setFieldErrors({ otp: 'Code expiré. Demandez un nouveau code.' });
        setOtpTimer(0);
      } else {
        setError(error.message || 'Erreur lors de la vérification');
      }
    } finally {
      setLoading(false);
    }
  };

  // Renvoyer le code OTP
  const handleResendOtp = async () => {
    setLoading(true);
    setError('');
    
    try {
      let result;
      
      if (loginMethod === 'email') {
        result = await authLogin(otpEmail);
        if (result.success) {
          setOtpTimer(600); // 10 minutes
          setSuccess('Nouveau code envoyé par email !');
        }
      } else {
        result = await authLoginPhone(otpPhone);
        if (result.success) {
          setOtpTimer(180); // 3 minutes
          setSuccess('Nouveau code envoyé par SMS !');
        }
      }
      
      if (result.success) {
        setOtpCode('');
      } else {
        setError(result.message || 'Erreur lors de l\'envoi du code');
      }
    } catch (error) {
      setError(error.message || 'Erreur lors de l\'envoi du code');
    } finally {
      setLoading(false);
    }
  };

  // Renvoyer le code de vérification
  const handleResendVerificationCode = async () => {
    setVerificationCode('');
    await handleSendVerificationCode();
  };

  // Basculer entre inscription et connexion
  const toggleMode = () => {
    setIsLoginMode(!isLoginMode);
    setError('');
    setSuccess('');
    setFieldErrors({});
    setShowOtpInput(false);
    setOtpCode('');
    setPhoneReadOnly(false);
    setShowVerificationStep(false);
    if (!isLoginMode) {
      setLoginMethod('phone'); // Par défaut sur téléphone
    }
  };

  // Changer de méthode de connexion
  const switchLoginMethod = (method) => {
    setLoginMethod(method);
    setError('');
    setSuccess('');
    setFieldErrors({});
    setShowOtpInput(false);
    setOtpCode('');

    if (!isPhoneLocked) {
      setPhoneReadOnly(false);
    }

    if (method === 'phone' && registerData.telephone && isPhoneLocked) {
      setLoginPhone(registerData.telephone);
    }

    // 🔐 Si on bascule en email et que l'email existe, on le pré-remplit
    if (method === 'email' && registerData.email.trim()) {
      setLoginEmail(registerData.email.trim());
    }
  };

  // Gestion des changements de champs
  const handleRegisterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRegisterData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Détection de l'opérateur en temps réel sur le champ téléphone
    if (name === 'telephone') {
      setPhoneOperator(detectPhoneOperator(value));
    }
    
    // Effacer l'erreur du champ lors de la modification
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Formatage du timer
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="register-page">
      {/* En-tête avec logo */}
      <div className="register-header">
        <img src={logoImage} alt="Boissons du Cameroun" className="register-logo" />
      </div>

      {/* Contenu principal */}
      <div className="register-content">
        <div className="register-container">
          {/* Messages de succès/erreur */}
          {success && (
            <div className="alert alert-success">
              {success}
            </div>
          )}
          
          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {/* Étape de vérification post-inscription */}
          {showVerificationStep && (
            <>
              <h1 className="register-title">Vérification du compte</h1>
              <p className="register-subtitle">
                Pour sécuriser votre compte, veuillez vérifier votre {verificationMethod === 'phone' ? 'numéro de téléphone' : 'adresse email'}
              </p>

              {!verificationCodeSent ? (
                // Étape 1: Confirmation du numéro/email
                <form className="register-form" onSubmit={(e) => { e.preventDefault(); handleSendVerificationCode(); }}>
                  <div className="form-group">
                    <label htmlFor="verificationValue" className="form-label">
                      {verificationMethod === 'phone' ? 'Numéro de téléphone' : 'Adresse email'}
                    </label>
                    <input
                      type={verificationMethod === 'phone' ? 'tel' : 'email'}
                      id="verificationValue"
                      className="form-control"
                      value={verificationData.value}
                      onChange={(e) => setVerificationData(prev => ({ ...prev, value: e.target.value }))}
                      disabled={!verificationData.editable || loading}
                    />
                    {verificationData.editable && verificationData.value !== verificationData.originalValue && (
                      <span className="form-hint">
                        ⚠️ Vous avez modifié le {verificationMethod === 'phone' ? 'numéro' : 'l\'email'}. 
                        Assurez-vous qu'il est correct.
                      </span>
                    )}
                  </div>

                  <button 
                    type="submit" 
                    className="register-button"
                    disabled={loading || !verificationData.value}
                  >
                    {loading ? 'Envoi en cours...' : 'Recevoir le code de vérification'}
                  </button>

                  <button 
                    type="button" 
                    className="register-button-secondary"
                    onClick={handleSkipVerification}
                    disabled={loading}
                  >
                    Vérifier plus tard
                  </button>

                  <p className="verification-info">
                    💡 La vérification est requise pour passer commande
                  </p>
                </form>
              ) : (
                // Étape 2: Saisie du code
                <form className="register-form" onSubmit={handleVerifyCode}>
                  <div className="verification-contact-info">
                    <p>Code envoyé {verificationMethod === 'phone' ? 'au' : 'à'} : <strong>{verificationData.value}</strong></p>
                    <button
                      type="button"
                      className="modify-link"
                      onClick={handleModifyVerificationData}
                      disabled={loading}
                    >
                      Modifier
                    </button>
                  </div>

                  {otpTimer > 0 && (
                    <div className="otp-timer">
                      Code valide pendant : {formatTimer(otpTimer)}
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="verificationCode" className="form-label">
                      Code de vérification ({verificationMethod === 'phone' ? '4 chiffres' : '6 chiffres'})
                    </label>
                    <input
                      type="text"
                      id="verificationCode"
                      className="form-control otp-input"
                      placeholder={verificationMethod === 'phone' ? '1234' : '123456'}
                      value={verificationCode}
                      onChange={(e) => {
                        const maxLength = verificationMethod === 'phone' ? 4 : 6;
                        const value = e.target.value.replace(/\D/g, '').slice(0, maxLength);
                        setVerificationCode(value);
                      }}
                      maxLength={verificationMethod === 'phone' ? '4' : '6'}
                      disabled={loading}
                      autoComplete="one-time-code"
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="register-button"
                    disabled={loading || !verificationCode}
                  >
                    {loading ? 'Vérification...' : 'Vérifier'}
                  </button>

                  <div className="register-footer">
                    <p className="register-login-text">
                      Code non reçu ?{' '}
                      <button
                        type="button"
                        className="register-login-link"
                        onClick={handleResendVerificationCode}
                        disabled={loading || otpTimer > (verificationMethod === 'phone' ? 120 : 540)}
                      >
                        Renvoyer
                      </button>
                    </p>
                    <p className="register-login-text">
                      <button
                        type="button"
                        className="register-login-link"
                        onClick={handleSkipVerification}
                        disabled={loading}
                      >
                        Vérifier plus tard
                      </button>
                    </p>
                  </div>
                </form>
              )}
            </>
          )}

          {/* Mode Inscription */}
          {!isLoginMode && !showOtpInput && !showVerificationStep && (
            <>
              <h1 className="register-title">Créer un compte</h1>
              <p className="register-subtitle">
                Inscrivez-vous pour commander vos boissons préférées
              </p>
			  <div className="login-banner">
				<p>Déjà inscrit ?</p>
				<button
				  type="button"
				  className="login-banner-button"
				  onClick={toggleMode}
				  disabled={loading}
				>
				  Se connecter
				</button>
			  </div>

              <form className="register-form" onSubmit={handleRegisterSubmit}>
                {/* NOUVEAU - Champ Nom (optionnel) */}
                <div className="form-row">
				  <div className="form-group form-group-half">
					<label htmlFor="prenom" className="form-label">
					  Prénom <span className="required">*</span>
					</label>
					<input
					  type="text"
					  id="prenom"
					  name="prenom"
					  className={`form-control ${fieldErrors.prenom ? 'error' : ''}`}
					  placeholder="Jean"
					  value={registerData.prenom}
					  onChange={handleRegisterChange}
					  disabled={loading}
					/>
					{fieldErrors.prenom && (
					  <span className="error-message">{fieldErrors.prenom}</span>
					)}
				  </div>
				  
				  <div className="form-group form-group-half">
					<label htmlFor="nom" className="form-label">
					  Nom <span className="form-optional">(optionnel)</span>
					</label>
					<input
					  type="text"
					  id="nom"
					  name="nom"
					  className={`form-control ${fieldErrors.nom ? 'error' : ''}`}
					  placeholder="Dupont"
					  value={registerData.nom}
					  onChange={handleRegisterChange}
					  disabled={loading}
					/>
				  </div>
				</div>

                {/* Téléphone */}
                <div className="form-group">
                  <label htmlFor="telephone" className="form-label">
                    Numéro de téléphone <span className="required">*</span>
                  </label>
                  <div className="phone-input-wrapper">
                    <input
                      type="tel"
                      id="telephone"
                      name="telephone"
                      className={`form-control ${
                        fieldErrors.telephone ? 'is-invalid' :
                        phoneOperator === 'valid' ? 'is-valid' :
                        phoneOperator === 'invalid' ? 'is-invalid' : ''
                      }`}
                      placeholder="Ex : 699332211"
                      value={registerData.telephone}
                      onChange={handleRegisterChange}
                      disabled={loading}
                    />
                    {phoneOperator === 'valid' && (
                      <span className="phone-operator-badge valid">✓</span>
                    )}
                    {phoneOperator === 'invalid' && (
                      <span className="phone-operator-badge invalid">✗</span>
                    )}
                  </div>
                  {/* Hint visuel permanent — opérateurs compatibles */}
                  <div className="phone-operator-hint">
                    <span className="operator-badge orange">Orange</span>
                    <span className="operator-badge mtn">MTN</span>
                    <span className="operator-hint-text">uniquement</span>
                  </div>
                  {/* Message d'erreur opérateur (avant soumission) */}
                  {phoneOperator === 'invalid' && !fieldErrors.telephone && (
                    <span className="form-error">
                      Ce numéro n'est pas compatible. Utilisez un numéro Orange ou MTN.
                    </span>
                  )}
                  {fieldErrors.telephone && (
                    <span className="form-error">{fieldErrors.telephone}</span>
                  )}
                </div>

                {/* Email */}
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Adresse email <span className="form-optional">(optionnel)</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`form-control ${fieldErrors.email ? 'is-invalid' : ''}`}
                    placeholder="Ex : jean@example.com"
                    value={registerData.email}
                    onChange={handleRegisterChange}
                    disabled={loading}
                  />
                  <span className="form-hint">
                    L'email vous permettra de recevoir vos factures
                  </span>
                  {fieldErrors.email && (
                    <span className="form-error">{fieldErrors.email}</span>
                  )}
                </div>

                {/* Checkbox conditions */}
                <div className="form-checkbox">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    name="acceptTerms"
                    className="checkbox-input"
                    checked={registerData.acceptTerms}
                    onChange={handleRegisterChange}
                    disabled={loading}
                  />
                  <label htmlFor="acceptTerms" className="checkbox-label">
                    J'accepte les conditions d'utilisation et la politique de confidentialité
                  </label>
                </div>
                {fieldErrors.acceptTerms && (
                  <span className="form-error">{fieldErrors.acceptTerms}</span>
                )}

                {/* Checkbox newsletter - visible seulement si email fourni */}
                {registerData.email.trim() && (
                  <div className="form-checkbox">
                    <input
                      type="checkbox"
                      id="acceptNewsletter"
                      name="acceptNewsletter"
                      className="checkbox-input"
                      checked={registerData.acceptNewsletter}
                      onChange={handleRegisterChange}
                      disabled={loading}
                    />
                    <label htmlFor="acceptNewsletter" className="checkbox-label">
                      Je souhaite recevoir les offres et promotions par email
                    </label>
                  </div>
                )}

                {/* Bouton de soumission */}
                <button 
                  type="submit" 
                  className="register-button"
                  disabled={loading}
                >
                  {loading ? 'Création en cours...' : 'Créer mon compte'}
                </button>

                {/* Lien connexion */}
                
              </form>
            </>
          )}

          {/* Mode Connexion - Étape 1: Email ou Téléphone */}
          {isLoginMode && !showOtpInput && !showVerificationStep && (
            <>
              <h1 className="register-title">Se connecter</h1>
              <p className="register-subtitle">
                Choisissez votre méthode de connexion
              </p>

              {/* Onglets de sélection */}
              <div className="login-tabs">
                <button
                  type="button"
                  className={`login-tab ${loginMethod === 'phone' ? 'active' : ''}`}
                  onClick={() => switchLoginMethod('phone')}
                  disabled={loading}
                >
                  Par SMS
                </button>
                <button
                  type="button"
                  className={`login-tab ${loginMethod === 'email' ? 'active' : ''}`}
                  onClick={() => switchLoginMethod('email')}
                  disabled={loading}
                >
                  Par Email
                </button>
              </div>

              <form className="register-form" onSubmit={handleLoginSubmit}>
                {loginMethod === 'email' ? (
                  <div className="form-group">
                    <label htmlFor="loginEmail" className="form-label">
                      Adresse email
                    </label>
                    <input
                      type="email"
                      id="loginEmail"
                      className={`form-control ${fieldErrors.email ? 'is-invalid' : ''}`}
                      placeholder="jean@example.com"
                      value={loginEmail}
                      onChange={(e) => {
                        setLoginEmail(e.target.value);
                        if (fieldErrors.email) {
                          setFieldErrors(prev => ({ ...prev, email: '' }));
                        }
                      }}
                      disabled={loading}
                    />
                    {fieldErrors.email && (
                      <span className="form-error">{fieldErrors.email}</span>
                    )}
                  </div>
                ) : (
                  <div className="form-group">
                    <label htmlFor="loginPhone" className="form-label">
                      Numéro de téléphone
                    </label>
                    <input
                      type="tel"
                      id="loginPhone"
                      className={`form-control ${fieldErrors.phone ? 'is-invalid' : ''}`}
                      placeholder="612345678"
                      value={loginPhone}
                      onChange={(e) => {
                        if (!phoneReadOnly) {
                          setLoginPhone(e.target.value);
                          if (fieldErrors.phone) {
                            setFieldErrors(prev => ({ ...prev, phone: '' }));
                          }
                        }
                      }}
                      disabled={loading}
                      readOnly={phoneReadOnly}
                    />
                    {fieldErrors.phone && (
                      <span className="form-error">{fieldErrors.phone}</span>
                    )}
                    {phoneReadOnly && (
                      <span className="form-hint">
                        Ce numéro est déjà enregistré. Cliquez sur "S'inscrire" pour utiliser un autre numéro.
                      </span>
                    )}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="register-button"
                  disabled={loading}
                >
                  {loading ? 'Envoi en cours...' : 'Recevoir le code de connexion'}
                </button>

                <div className="register-footer">
                  <p className="register-login-text">
                    Pas encore de compte ?{' '}
                    <button
                      type="button"
                      className="register-login-link"
                      onClick={toggleMode}
                      disabled={loading}
                    >
                      S'inscrire
                    </button>
                  </p>
                </div>
              </form>
            </>
          )}

          {/* Mode Connexion - Étape 2: Code OTP */}
          {showOtpInput && !showVerificationStep && (
            <>
              <h1 className="register-title">Vérification</h1>
              <p className="register-subtitle">
                Entrez le code {loginMethod === 'email' ? 'à 6 chiffres' : 'à 4 chiffres'} envoyé {loginMethod === 'email' ? `à ${otpEmail}` : `au ${otpPhone}`}
              </p>
              
              {otpTimer > 0 && (
                <div className="otp-timer">
                  Code valide pendant : {formatTimer(otpTimer)}
                </div>
              )}

              <form className="register-form" onSubmit={handleOtpSubmit}>
                <div className="form-group">
                  <label htmlFor="otpCode" className="form-label">
                    Code de vérification
                  </label>
                  <input
                    type="text"
                    id="otpCode"
                    className={`form-control otp-input ${fieldErrors.otp ? 'is-invalid' : ''}`}
                    placeholder={loginMethod === 'email' ? '123456' : '1234'}
                    value={otpCode}
                    onChange={(e) => {
                      const maxLength = loginMethod === 'email' ? 6 : 4;
                      const value = e.target.value.replace(/\D/g, '').slice(0, maxLength);
                      setOtpCode(value);
                      if (fieldErrors.otp) {
                        setFieldErrors(prev => ({ ...prev, otp: '' }));
                      }
                    }}
                    maxLength={loginMethod === 'email' ? '6' : '4'}
                    disabled={loading}
                    autoComplete="one-time-code"
                  />
                  {fieldErrors.otp && (
                    <span className="form-error">{fieldErrors.otp}</span>
                  )}
                </div>

                <button 
                  type="submit" 
                  className="register-button"
                  disabled={loading || !otpCode}
                >
                  {loading ? 'Vérification...' : 'Valider'}
                </button>

                <div className="register-footer">
                  <p className="register-login-text">
                    Vous n'avez pas reçu le code ?{' '}
                    <button
                      type="button"
                      className="register-login-link"
                      onClick={handleResendOtp}
                      disabled={loading || (loginMethod === 'email' ? otpTimer > 540 : otpTimer > 120)}
                    >
                      Renvoyer
                    </button>
                  </p>
                  <p className="register-login-text">
                    <button
                      type="button"
                      className="register-login-link"
                      onClick={() => {
                        setShowOtpInput(false);
                        setOtpCode('');
                        setOtpTimer(0);
                        setPhoneReadOnly(false);
                      }}
                      disabled={loading}
                    >
                      Utiliser {loginMethod === 'email' ? 'une autre adresse email' : 'un autre numéro'}
                    </button>
                  </p>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;